import * as Yup from "yup";
import { RedPacketType } from "~proto";

/** 与 validator `nickname` 一致：中文等按 2、英文数字按 1 计，总长 ≤ 18 */
function nicknameDisplayUnits(value: string): number {
  const engAndNum = (value.match(/[a-zA-Z0-9]/g) ?? []).join("")
  const otherLength = value.length - engAndNum.length
  return otherLength * 2 + engAndNum.length
}

export const UpdateUserDisplayNameSchema = Yup.object({
  name: Yup.string()
    .required("昵称不能为空白")
    .test("trim", "昵称不能为空白", (v) => typeof v === "string" && v.trim().length > 0)
    .test("nickname-length", "昵称超过长度", (v) => {
      if (!v) return true
      return nicknameDisplayUnits(v) <= 18
    }),
})

export const UpdateUserUserNameSchema = Yup.object({
  username: Yup.string()
    .required("账号不能为空白")
    .test("trim", "账号不能为空白", (v) => typeof v === "string" && v.trim().length > 0)
    .test("username-length", "账号须为5-8位数字与字母组合", (v) => {
      if (!v) return true
      const s = v.trim()
      return s.length >= 5 && s.length <= 8
    })
    .test("username-format", "须为字母与数字组合", (v) => {
      if (!v) return true
      const s = v.trim()
      return /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]+$/.test(s)
    }),
})

export const UpdateUserDisplayDescSchema = Yup.object({
  desc: Yup.string()
    .required("个性签名不能为空白")
    .test("trim", "个性签名不能为空白", (v) => typeof v === "string" && v.trim().length > 0)
    .test("desc-length", "个性签名超过长度", (v) => {
      if (!v) return true
      return v.length <= 200
    }),
})

export const RedPacketSchema = (maxAmount?: number | string) => {
  const groupLuckMax =
    typeof maxAmount === "number"
      ? maxAmount
      : typeof maxAmount === "string" && maxAmount.trim() !== ""
        ? Number(maxAmount)
        : undefined
  const resolvedGroupLuckMax =
    typeof groupLuckMax === "number" && Number.isFinite(groupLuckMax) ? groupLuckMax : 10000

  return Yup.object().shape({
  type: Yup.number().oneOf([
    RedPacketType.LUCK,
    RedPacketType.NORMAL,
    RedPacketType.EXCLUSIVE
  ]),

  amount: Yup.number()
    .typeError("金额格式不正确")
    .positive("金额必须大于 0")
    .required("请输入金额")
    .test("amount-max", function (value) {
      if (value == null || typeof value !== "number" || Number.isNaN(value)) {
        return true
      }
      const parent = this.parent as {
        type?: number
        chatTypeEnum?: string
      } | null
      if (!parent) return true
      const { type, chatTypeEnum } = parent
      if (chatTypeEnum === "GROUP" && type === RedPacketType.LUCK) {
        return (
          value <= resolvedGroupLuckMax ||
          this.createError({ message: `群红包总金额不超过${resolvedGroupLuckMax}元` })
        )
      }
      if (chatTypeEnum === "GROUP" && type === RedPacketType.NORMAL) {
        return (
          value <= resolvedGroupLuckMax ||
          this.createError({ message: `单个红包金额不超过${resolvedGroupLuckMax}元` })
        )
      }
      return (
        value <= resolvedGroupLuckMax ||
        this.createError({ message: `红包金额不超过${resolvedGroupLuckMax}元` })
      )
    }),

  count: Yup.number()
    .typeError("格式错误")
    .integer("必须是整数")
    .min(1, "红包个数为 1-100")
    .max(100, "红包个数为 1-100")
    .when(["type", "chatTypeEnum"], {
      is: (type, chatTypeEnum) =>
        chatTypeEnum === "GROUP" && type !== RedPacketType.EXCLUSIVE,
      then: (schema) => schema.required("请输入红包个数"),
      otherwise: (schema) => schema.notRequired(),
    }),

  remark: Yup.string().max(50, "最多 50 字")
  })
}

export const TransferSchema = (maxAmount?: number | string) => {
  const resolvedMaxAmount =
    typeof maxAmount === "number"
      ? maxAmount
      : typeof maxAmount === "string" && maxAmount.trim() !== ""
        ? Number(maxAmount)
        : undefined
  const resolvedResolvedMaxAmount =
    typeof maxAmount === "number" && Number.isFinite(resolvedMaxAmount) ? resolvedMaxAmount : 10000
  return Yup.object().shape({
    amount: Yup.number()
      .typeError("金额格式不正确")
      .positive("金额必须大于 0")
      .required("请输入金额")
      .test("amount-max", function (value) {
        if (value == null || typeof value !== "number" || Number.isNaN(value)) {
          return true
        }
        return value <= resolvedResolvedMaxAmount || this.createError({ message: `转账金额不超过${resolvedResolvedMaxAmount}元` })
      }),
    wp_password: Yup.string()
      .required("请输入支付密码")
      .min(6, "支付密码至少6位"),
  })
}

export const UpwardFundsSchema = Yup.object().shape({
  amount: Yup.number()
    .typeError("金额格式不正确")
    .positive("金额必须大于 0")
    .required("请输入金额")
})

export const notDisturSchema = Yup.object().shape({
    hour: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .positive("必须大于 0")
      .integer("必须是整数")
      .nullable(),

    minute: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .positive("必须大于 0")
      .integer("必须是整数")
      .nullable(),
  }).test(
    "hour-or-minute-required",
    "小时和分钟至少填写一个",
    (values) => {
      if (!values) return false
      const { hour, minute } = values
      return !!hour || !!minute
    }
  )

export const AddGroupSchema = Yup.object().shape({
  title: Yup.string()
    .required("请输入群名称")
    .max(20, "最多 20 字"),
});

export const AddChannelSchema = Yup.object().shape({
  title: Yup.string()
    .required("请输入频道名称")
    .max(20, "最多 20 字"),
});
